import React, { useEffect, useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, SkipForward, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS_STYLE = { ok: "text-palm", skip: "text-gold", error: "text-crimson" };
const STATUS_ICON = { ok: CheckCircle2, skip: SkipForward, error: AlertTriangle };

const PreviewRow = ({ r }) => {
  const Icon = STATUS_ICON[r.status];
  return (
    <tr className="border-b border-gold/15 text-xs" data-testid={`import-row-${r.line}`}>
      <td className="py-1.5 pr-2 text-muted-foreground">{r.line}</td>
      <td className="py-1.5 pr-2 font-medium">{r.name || "—"}</td>
      <td className="py-1.5 pr-2 font-mono-accent">{r.email || <span className="rounded bg-stone-200 px-1.5 text-[10px] font-semibold uppercase text-stone-700">no login</span>}</td>
      <td className="py-1.5 pr-2">{r.role}</td>
      <td className={`py-1.5 ${STATUS_STYLE[r.status]}`}><span className="flex items-center gap-1"><Icon size={12} /> {r.problems.join(", ") || "ready"}</span></td>
    </tr>
  );
};

const JobProgress = ({ jobId, onDone }) => {
  const [job, setJob] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const { data } = await api.get(`/admin/import/jobs/${jobId}`);
      if (!alive) return;
      setJob(data);
      if (data.status !== "done") setTimeout(tick, 1500); else onDone?.();
    };
    tick();
    return () => { alive = false; };
  }, [jobId, onDone]);
  if (!job) return <Loader2 className="animate-spin" />;
  const count = (s) => job.results.filter((r) => r.status === s).length;
  const pct = Math.round((job.processed / job.total) * 100);
  return (
    <div className="space-y-3" data-testid="import-progress">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gold/20"><div className="h-full bg-palm transition-[width]" style={{ width: `${pct}%` }} /></div>
      <p className="text-sm">{job.status === "done" ? "Import complete" : "Importing…"} — {job.processed}/{job.total}</p>
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <div className="rounded-lg bg-palm/10 p-2"><p className="text-2xl font-bold text-palm" data-testid="import-created">{count("created")}</p>created</div>
        <div className="rounded-lg bg-gold/15 p-2"><p className="text-2xl font-bold text-gold" data-testid="import-skipped">{count("skipped")}</p>skipped</div>
        <div className="rounded-lg bg-crimson/10 p-2"><p className="text-2xl font-bold text-crimson" data-testid="import-failed">{count("failed")}</p>failed</div>
      </div>
      {job.status === "done" && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadPdf(`/admin/import/jobs/${jobId}/results.csv`, "import-results.csv")} data-testid="import-results-csv"><Download size={14} className="mr-1" /> Results CSV</Button>
          {!job.send_invites && count("created") > 0 && (
            <Button size="sm" className="bg-crimson" onClick={async () => { const { data } = await api.post(`/admin/import/jobs/${jobId}/send-invites`); toast.success(`Sending ${data.queued} invite(s)`); }} data-testid="import-send-invites">Send set-password invites now</Button>
          )}
        </div>
      )}
    </div>
  );
};

export const BulkImport = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendInvites, setSendInvites] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const reset = () => { setPreview(null); setJobId(null); };
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const run = async () => {
    const rows = preview.rows.filter((r) => r.status === "ok").map(({ line, status, problems, ...r }) => r);
    setBusy(true);
    try { const { data } = await api.post("/admin/import/run", { rows, send_invites: sendInvites }); setJobId(data.job_id); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="bulk-import-btn"><FileSpreadsheet size={15} /> Import members (CSV)</Button>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">Bulk member import</DialogTitle></DialogHeader>
        {jobId ? <JobProgress jobId={jobId} onDone={onImported} /> : !preview ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload a CSV with columns <code className="font-mono-accent">first_name, last_name</code> (required) and <code className="font-mono-accent">email</code> (optional — rows without email become record-only members with no login until an email is added) plus optional <code className="font-mono-accent">phone, member_id, join_date, dob, address, lineage_status, role</code>. Dates accept <code className="font-mono-accent">MM/DD/YYYY</code> or <code className="font-mono-accent">YYYY-MM-DD</code>; role is case-insensitive (e.g. <code className="font-mono-accent">Member</code>, <code className="font-mono-accent">treasurer</code>). Each new member receives a one-time set-password email.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => downloadPdf("/admin/import/template.csv", "member-import-template.csv")} data-testid="import-template-btn"><Download size={15} className="mr-1" /> Download template</Button>
              <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-crimson" data-testid="import-upload-btn">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Upload size={15} className="mr-1" />} Choose CSV</Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={pick} data-testid="import-file-input" />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="import-summary">
              <span className="text-palm font-semibold">{preview.summary.ok} ready</span>
              <span className="text-gold font-semibold">{preview.summary.skip} already exist</span>
              <span className="text-crimson font-semibold">{preview.summary.error} with errors</span>
            </div>
            <div className="max-h-72 overflow-auto rounded-lg border border-gold/20">
              <table className="w-full text-left"><thead className="sticky top-0 bg-parchment-card text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-1.5">#</th><th className="p-1.5">Name</th><th className="p-1.5">Email</th><th className="p-1.5">Role</th><th className="p-1.5">Check</th></tr></thead>
                <tbody>{preview.rows.map((r) => <PreviewRow key={r.line} r={r} />)}</tbody></table>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-parchment-card p-3">
              <div><Label>Send set-password invites now</Label><p className="text-xs text-muted-foreground">Turn off to create accounts first and send invites later.</p></div>
              <Switch checked={sendInvites} onCheckedChange={setSendInvites} data-testid="import-send-invites-switch" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="import-back">Choose another file</Button>
              <Button onClick={run} disabled={busy || preview.summary.ok === 0} className="flex-1 bg-crimson" data-testid="import-run-btn">Create {preview.summary.ok} account(s)</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
